import {Global} from '@emotion/react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/react';
import {
  AuthUrls,
  BrowserMessageReceiveType,
  BrowserMessageType,
  CommonMessageType,
  registerElectronMessageReceiver,
  sendElectronMessage,
  VersionInfo,
} from 'electron-communication-lib/BrowserForApp';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {observer, useLocalObservable} from 'mobx-react-lite';
import moment from 'moment';
import React, {Suspense, useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useMatch,
} from 'react-router-dom';
import {Divider} from 'webapp-lib/Components';
import {AuthWorkflowHeader} from 'webapp-lib/DesignComponents';
import {
  showSnackbar,
  SnackbarType,
  useSnackbarContainer,
} from 'webapp-lib/Functions';
import {
  isInputElement,
  Language,
  useKeyboard,
  useMountEffect,
} from 'webapp-lib/utils';

import 'moment/dist/locale/de';

import {Loading, NavigationBar} from '#';
import handledNotificationBackend from '@lexian/api/notifications-api';
import backend from '@lexian/api/support-api';
import handledUserBackend from '@lexian/api/user-api';
import {useLockBlocker} from '_/hooks';
import {useCommunicationPushNotification} from '_/hooks/communication/useCommunicationPushNotification';
import {useEmailAccountAuthenticationFailedPushNotification} from '_/hooks/communication/useEmailAccountAuthenticationFailedPushNotification';
import {useBrowserMessageExchange} from '_/hooks/useBrowserMessageExchange';
import {useReloadUserInformation} from '_/hooks/useReloadUserInformation';
import {useVersionInfo} from '_/hooks/useVersionInfo';
import i18n from '_/i18n';
import {OAuth2} from '_/sharedComponents/OAuth2/OAuth2';
import {browserCapabilitiesState} from '_/state/browserCapabilities';
import {failedMailAuthentication} from '_/state/failedMailAuthentication';
import {featureFlagState} from '_/state/featureFlags';
import {languageState} from '_/state/language';
import {masterdataState} from '_/state/masterdata';
import {pendingEmailState} from '_/state/pendingEmail';
import {userState} from '_/state/user';
import {DocumentUploadStore} from '_/store/DocumentUpload.store';
import {TabBarStore} from '_/store/tabBar.store';
import {globalStyles} from '_/styles/global';
import {
  AllowedLocaleDateFormats,
  DocumentUploadContext,
  getRouteUrl,
  TabBarContext,
} from '_/utilities';
import {setupSentry} from '_/utilities/sentrySetup';

import {CasusSubRoutes} from './CasusSubroutes';
import {GlobalErrorBoundry} from './components/GlobalErrorBoundry';
import {Header} from './components/Header';
import {Login, PWReset} from './components/LoginMasks';
import {MaintenanceOverlay} from './components/MaintenanceOverlay';
import {
  VersionMismatchWarning,
  VersionUpdateInfo,
} from './components/VersionInfoWarning';
import {useOpenFileCommand} from './components/useOpenFileCommand';
import {useFilesInUse, UseFilesInUseActions} from './hooks/useFilesInUse';
import {Container} from './styles';
import {loadMasterdata} from './utils/loadMasterdata';
import {
  PendingUploadTriggers,
  showPendingUploadsConfirmationDialog,
} from './utils/showPendingUploadsConfirmationDialog';
import {useHandleCloseRequested} from './utils/useHandleCloseRequested';
import {useHandlePing} from './utils/useHandlePing';
import {useHandleReloadRequested} from './utils/useHandleReloadRequested';
import {useHandleUnsavedChangesQuery} from './utils/useHandleUnsavedChangesQuery';
import {useTranslatedNavModules} from './utils/useTranslatedNavModules';

const CockpitModule = React.lazy(
  () => import('../modules/cockpit/CockpitModule')
);
const CommunicationModule = React.lazy(
  () => import('../modules/communication')
);
const TaskModule = React.lazy(
  () => import('../modules/tasks/exports/TaskTableView')
);
const ContactsModule = React.lazy(
  () => import('../modules/contacts/ContactsModule')
);
const InvoicingModule = React.lazy(() => import('../modules/invoicing'));

const ObligationsModule = React.lazy(() => import('../modules/obligations'));
const SearchModule = React.lazy(() => import('../modules/search/Search'));
const SettingsModule = React.lazy(() => import('../modules/settings/settings'));

const TimeTrackingModule = React.lazy(() => import('../modules/time-tracking'));
const UserModule = React.lazy(() => import('../modules/user'));

const SubrouteModels = createRoutesFromElements(
  <>
    <Route element={<InvoicingModule />} path='invoicing/*' />
    <Route element={<CockpitModule />} path='cockpit/*' />
    <Route element={<ContactsModule />} path='contacts/*'>
      <Route element={<ContactsModule />} path=':activeContactId/*' />
    </Route>
    <Route element={<CommunicationModule />} path='communication/*' />
    <Route element={<TaskModule />} path='tasks/*' />
    <Route element={<ObligationsModule />} path='obligations/*' />
    <Route
      element={<ObligationsModule />}
      path='obligations/:activeObligationId/'
    />
    <Route element={<SettingsModule />} path='settings/*' />
    <Route element={<UserModule />} path='user/*' />
    <Route element={<TimeTrackingModule />} path='time-tracking/*' />
    <Route element={<SearchModule />} path='search/' />
    <Route path='casus/*'>{CasusSubRoutes}</Route>
  </>
);

const globalTabBarStore = new TabBarStore();

function UApplication(): React.ReactElement {
  const [language, setLanguage] = useAtom(languageState);

  const [filesInUseDialog, filesInUseActions] = useFilesInUse();

  useBrowserMessageExchange({
    message: {type: CommonMessageType.GET_USER_LANGUAGE, payload: null},
    responsePayloadHandler: (response: string) => {
      setLanguage(response as Language);
    },
  });

  const setBrowserCapabilities = useSetAtom(browserCapabilitiesState);
  useBrowserMessageExchange({
    message: {
      type: BrowserMessageType.LEXIAN_BROWSER_CAPABILITIES,
      payload: null,
    },
    responsePayloadHandler: setBrowserCapabilities,
    exchangeOptions: {onTimeout: () => undefined},
  });

  useEffect(() => {
    i18n.changeLanguage(language);
    sendElectronMessage({
      type: CommonMessageType.SET_USER_LANGUAGE,
      payload: language,
    });

    const lang = language.startsWith('de')
      ? AllowedLocaleDateFormats.DE
      : AllowedLocaleDateFormats.EN;
    moment.locale(lang, {
      // Set first day of week to always be Monday
      week: {
        dow: 1,
      },
    });
  }, [language]);

  const loginHeader = useMemo(
    () => (
      <AuthWorkflowHeader
        changeLanguage={(lang: Language) => setLanguage(lang)}
        language={language}
      />
    ),
    // this is mistakingly not recognized as needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const [browserUpdateInfo, setBrowserUpdateInfo] = useState<VersionInfo>();
  useMountEffect(() => {
    registerElectronMessageReceiver(
      BrowserMessageReceiveType.UPDATE_AVAILABLE,
      setBrowserUpdateInfo
    );
  });

  function update(): void {
    filesInUseActions.clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.UPDATE_APPLICATION,
    });
  }

  const router = createBrowserRouter([
    {
      path: AuthUrls.LOGIN,
      element: (
        <Login
          loginHeader={loginHeader}
          VersionInfo={
            <WarningContainer>
              <VersionUpdateInfo
                browserUpdateInfo={browserUpdateInfo}
                onUpdate={update}
              />
            </WarningContainer>
          }
        />
      ),
    },
    {
      path: AuthUrls.PW_RESET,
      element: <PWReset loginHeader={loginHeader} />,
    },
    {
      path: AuthUrls.OAUTH_REDIRECT,
      element: <OAuth2 />,
    },
    {
      path: '*',
      children: SubrouteModels,
      errorElement: <GlobalErrorBoundry />,
      element: (
        <ApplicationContent
          browserUpdateInfo={browserUpdateInfo}
          filesInUseActions={filesInUseActions}
        />
      ),
    },
  ]);

  return (
    <>
      <Helmet>
        <meta charSet='utf-8' />
        <link
          href={`${process.env.PUBLIC_URL}/apple-touch-icon.png`}
          rel='apple-touch-icon'
          sizes='180x180'
        />
        <link
          href={`${process.env.PUBLIC_URL}/favicon-32x32.png`}
          rel='icon'
          sizes='32x32'
          type='image/png'
        />
        <link
          href={`${process.env.PUBLIC_URL}/favicon-16x16.png`}
          rel='icon'
          sizes='16x16'
          type='image/png'
        />
        <link
          href={`${process.env.PUBLIC_URL}/site.webmanifest`}
          rel='manifest'
        />
        <link
          color='#2e4684'
          href={`${process.env.PUBLIC_URL}/safari-pinned-tab.svg`}
          rel='mask-icon'
        />
        <meta content='Lexian App' name='apple-mobile-web-app-title' />
        <meta content='Lexian App' name='application-name' />
        <meta content='#2e4684' name='msapplication-TileColor' />
        <meta content='#2e4684' name='theme-color' />

        <meta content='width=device-width, initial-scale=1' name='viewport' />
      </Helmet>
      <Global styles={globalStyles} />
      <RouterProvider router={router} />
      {filesInUseDialog}
    </>
  );
}

const UApplicationContent =
  // eslint-disable-next-line max-statements
  ({
    filesInUseActions,
    browserUpdateInfo,
  }: {
    filesInUseActions: UseFilesInUseActions;
    browserUpdateInfo: VersionInfo | undefined;
  }) => {
    const {snackbar} = useSnackbarContainer();
    const user = useAtomValue(userState);
    const setFeatureFlags = useSetAtom(featureFlagState);
    const [masterdata, setMasterdata] = useAtom(masterdataState);

    const {MODULES, SECONDARY_MODULES} = useTranslatedNavModules();

    const activeModule = useMatch('/:activeModule/*')?.params?.activeModule;

    const setHasPendingMail = useSetAtom(pendingEmailState);
    const setHasFailedMailAccount = useSetAtom(failedMailAuthentication);

    const version = useVersionInfo();

    const handleKeyDown = (event: KeyboardEvent) => {
      const hasCtrlModifier = event.ctrlKey || event.metaKey;

      if (
        hasCtrlModifier &&
        event.key === 'a' &&
        !isInputElement(event.target as HTMLElement)
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    useKeyboard(handleKeyDown);

    useOpenFileCommand();

    useEffect(() => {
      if (version) {
        setupSentry(version);
      }
    }, [version]);

    useEffect(() => {
      if (user) {
        Sentry.setUser({
          id: user.userId,
        });
      }
    }, [user]);

    useCommunicationPushNotification(() => setHasPendingMail(true));
    useEmailAccountAuthenticationFailedPushNotification((name, userName) => {
      setHasFailedMailAccount(true);
      showSnackbar({
        text: i18n.t('user:snackbar.error.failedEmailAccountAuthentication', {
          userName,
          name,
        }),
        type: SnackbarType.WARNING,
      });
    });

    const reloadUserInformation = useReloadUserInformation((userInfo) => {
      sendElectronMessage({
        payload: userInfo,
        type: BrowserMessageType.SEND_USER_INFO,
      });
    });
    useMountEffect(() => {
      reloadUserInformation();
    });

    useMountEffect(() => {
      (async () => {
        const data = await loadMasterdata();
        setMasterdata(data);
        backend.getFeatures({
          parameters: {},
          onSuccess: ({body}) => setFeatureFlags(body),
        });
      })();
    });

    // only on initial load, after it is synced via push notifications
    useMountEffect(() => {
      handledNotificationBackend.getEmailIndication({
        parameters: {},
        onSuccess: ({body}) => {
          if (body) {
            setHasPendingMail(true);
          }
        },
      });
    });

    useEffect(() => {
      handledUserBackend.getEmailAccounts({
        parameters: {},
        onSuccess: ({body}) => {
          const hasAtLeastOneFailedAccount = body.emailAccounts.some(
            (mailAccount) => mailAccount.state === 'AUTHENTICATION_FAILURE'
          );
          setHasFailedMailAccount(hasAtLeastOneFailedAccount);
        },
      });
    }, [setHasFailedMailAccount]);

    const documentUploadStore = useLocalObservable(
      () => new DocumentUploadStore()
    );

    function runPreconditionChecksAndExecute(
      triggerType: PendingUploadTriggers,
      onProceed: () => void
    ): void {
      // don't show reload warning in case no files are in progress
      if (
        documentUploadStore.pendingFiles.length > 0 ||
        documentUploadStore.failedFiles.length > 0
      ) {
        showPendingUploadsConfirmationDialog({
          trigger: triggerType,
          numberOfPendingFiles: documentUploadStore.pendingFiles.length,
          numberOfFailedFiles: documentUploadStore.failedFiles.length,
          proceed: onProceed,
        });
      } else {
        // execute reload
        onProceed();
      }
    }

    const tryExitOrLogout = (
      type: PendingUploadTriggers.CLOSE | PendingUploadTriggers.LOGOUT
    ) => {
      if (type === PendingUploadTriggers.CLOSE) {
        filesInUseActions.onClose(); // already shows pendingUpload dialog in HandleClose
        return;
      }

      runPreconditionChecksAndExecute(type, () => {
        filesInUseActions.onLogout();
      });
    };

    function tryUpdate() {
      runPreconditionChecksAndExecute(PendingUploadTriggers.UPDATE, () => {
        filesInUseActions.onUpdate();
      });
    }

    useHandlePing();
    useHandleUnsavedChangesQuery();
    useHandleCloseRequested(documentUploadStore);
    useHandleReloadRequested((onReload) =>
      runPreconditionChecksAndExecute(PendingUploadTriggers.RELOAD, onReload)
    );

    if (!activeModule) {
      return <Navigate to={getRouteUrl({module: 'cockpit'})} />;
    }

    return (
      <TabBarContext.Provider value={globalTabBarStore}>
        {user && masterdata ? (
          <DocumentUploadContext.Provider value={documentUploadStore}>
            <Container>
              <LockBlocker />
              {snackbar}
              <WarningContainer>
                <VersionUpdateInfo
                  browserUpdateInfo={browserUpdateInfo}
                  onUpdate={tryUpdate}
                />
                <Divider
                  aria-hidden='true'
                  className='Divider'
                  orientation='vertical'
                />
                <VersionMismatchWarning />
              </WarningContainer>
              <NavigationBar
                activeTabId={activeModule}
                secondaryTabs={SECONDARY_MODULES}
                tabs={MODULES}
              />
              <Header
                activeModule={activeModule}
                onLogout={() => tryExitOrLogout(PendingUploadTriggers.LOGOUT)}
                onQuit={() => tryExitOrLogout(PendingUploadTriggers.CLOSE)}
                onShowFilesInUse={() => {
                  filesInUseActions.showFilesInUse();
                }}
              />
              <main>
                <MaintenanceOverlay />
                <Suspense fallback={<Loading />}>
                  <Outlet />
                </Suspense>
              </main>
            </Container>
          </DocumentUploadContext.Provider>
        ) : (
          <Loading />
        )}
      </TabBarContext.Provider>
    );
  };
const ApplicationContent = observer(UApplicationContent);

const LockBlocker = () => {
  useLockBlocker();
  return null;
};

export const Application = observer(UApplication);

const WarningContainer = styled.div`
  grid-area: warning-bar;
  display: flex;
  gap: var(--gutter-small);
  background-color: var(--color-notification-important);
  border-bottom: 1px solid var(--color-core-grey);

  hr.Divider {
    border-color: var(--color-box-border);

    &:first-child,
    &:last-child {
      display: none;
    }
  }
`;
